import {
  useEffect, useState, useMemo, memo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  alpha
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useHistory } from 'react-router-dom';
import ROUTE from 'src/utils/routeConstants';
import { useDispatch, useSelector } from 'react-redux';
import { getCardList } from 'src/redux/cardSlice';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { cardTableHeaders, getAlignByColumnId } from 'src/utils/tableHeaders';
import CustomPagination from 'src/common/CustomPagination';
import { getFuelQuantity, getSelectedSiteFilterValue } from 'src/utils/getInitials';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

/* ---------- Status Pill ---------- */
const StatusPill = ({ active }) => (
  <Chip
    label={active ? 'Active' : 'Inactive'}
    color={active ? 'success' : 'error'}
    size="small"
    variant="outlined"
    sx={{ fontWeight: 600 }}
  />
);

StatusPill.propTypes = {
  active: PropTypes.bool.isRequired
};

/* ---------- Mobile Card ---------- */
const MobileCardView = memo(({ card, onViewClick }) => (
  <Card
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Typography variant="subtitle1" fontWeight={700}>
      {card.uid || 'N/A'}
    </Typography>

    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Vehicle:
        {' '}
        {card.vehicle_number || 'N/A'}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        Site:
        {' '}
        {card.site || 'N/A'}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        Type:
        {' '}
        {card.card_type || 'N/A'}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        Limit:
        {' '}
        {getFuelQuantity(card.limit_quantity) || '0 L'}
      </Typography>
    </Box>

    <Box sx={{ mt: 2 }}>
      <StatusPill active={card.active} />
    </Box>

    <Button
      fullWidth
      size="small"
      variant="outlined"
      sx={{ mt: 2 }}
      onClick={() => onViewClick(card.id)}
    >
      View Details
    </Button>
  </Card>
));

MobileCardView.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    uid: PropTypes.string,
    vehicle_number: PropTypes.string,
    site: PropTypes.string,
    card_type: PropTypes.string,
    limit_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    active: PropTypes.bool
  }).isRequired,
  onViewClick: PropTypes.func.isRequired
};

MobileCardView.displayName = 'MobileCardView';

/* ---------- Main Component ---------- */
const CardListTable = ({ refreshData, searchText, siteFilter }) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const { cardList, totalCount, loading } = useSelector((state) => state.cards);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const cardAlignById = useMemo(() => getAlignByColumnId(cardTableHeaders), []);
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(
      getCardList({
        limit,
        pageNo: 1,
        params: { search: searchText, site: selectedSite }
      })
    );
  }, [dispatch, limit, refreshData, searchText, selectedSite]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(
      getCardList({
        limit,
        pageNo: newPage + 1,
        params: { search: searchText, site: selectedSite }
      })
    );
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
  };

  const viewCardInfo = (cardId) => {
    history.push(`${ROUTE.cards}/${cardId}`);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayData = cardList?.slice().sort(getComparator(order, orderBy)) || [];

  if (loading) return <Loader />;

  return (
    <>
      {isMobile ? (
        <>
          {displayData.map((card) => (
            <MobileCardView
              key={card.id}
              card={card}
              onViewClick={viewCardInfo}
            />
          ))}

          <CustomPagination
            page={page}
            rowsPerPage={limit}
            count={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
          />
        </>
      ) : (
        <Box
          sx={{
            width: '100%',
            borderRadius: 2,
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              flex: 1,
              overflow: 'auto',
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: '#F9FAFB',
                borderBottom: `1px solid ${theme.palette.divider}`,
                zIndex: 2
              },
              '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                position: 'sticky',
                right: 0,
                zIndex: 3,
                backgroundColor: '#F9FAFB',
                boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
              }
            }}
          >
            <Table stickyHeader>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                headCells={cardTableHeaders}
                onRequestSort={handleRequestSort}
                sx={{
                  backgroundColor: '#F9FAFB',
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}
              />
              <TableBody>
                {displayData.length > 0 ? (
                  displayData.map((card) => (
                    <TableRow
                      hover
                      key={card.id}
                      sx={{
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { backgroundColor: '#F9FAFB' }
                      }}
                    >
                      <TableCell align={cardAlignById.id}>{card.id}</TableCell>
                      <TableCell align={cardAlignById.uid}>{card.uid}</TableCell>
                      <TableCell align={cardAlignById.vehicle_number}>{card.vehicle_number || 'N/A'}</TableCell>
                      <TableCell align={cardAlignById.site}>{card.site || 'N/A'}</TableCell>
                      <TableCell align={cardAlignById.card_category}>{card.card_category || 'N/A'}</TableCell>
                      <TableCell align={cardAlignById.per_filling_limit}>
                        {getFuelQuantity(card.per_filling_limit) || '0 L'}
                      </TableCell>
                      <TableCell align={cardAlignById.per_day_limit}>
                        {getFuelQuantity(card.per_day_limit) || '0 L'}
                      </TableCell>
                      <TableCell align={cardAlignById.limit_quantity}>
                        {getFuelQuantity(card.limit_quantity) || '0 L'}
                      </TableCell>
                      <TableCell align={cardAlignById.fill_limit_type}>{card.fill_limit_type || 'N/A'}</TableCell>
                      <TableCell align={cardAlignById.active}>
                        <StatusPill active={card.active} />
                      </TableCell>
                      <TableCell
                        align={cardAlignById.action}
                        sx={{
                          position: 'sticky',
                          right: 0,
                          zIndex: 1,
                          backgroundColor: '#FFFFFF',
                          boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                        }}
                      >
                        <Tooltip title="View Details">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              viewCardInfo(card.id);
                            }}
                            size="medium"
                            aria-label="View details"
                            sx={getTableActionIconButtonSx(theme)}
                          >
                            <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={cardTableHeaders.length} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        No Cards Found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <Divider sx={{ flexShrink: 0 }} />

          <Box sx={{ p: 1, flexShrink: 0, bgcolor: '#FFFFFF' }}>
            <CustomPagination
              page={page}
              rowsPerPage={limit}
              count={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

CardListTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default CardListTable;
